<script setup lang="ts">
import {computed, onMounted, onBeforeUnmount, ref} from 'vue'
import {type ChallengeModifier} from "@/api/dx-backend";
import DiceModifier from "@/components/DiceRoller/DiceModifier/DiceModifier.vue";

interface Props {
  modifiers: ChallengeModifier[];
  highlightedIndex?: number;
  isAnimating?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modifiers: () => [],
  highlightedIndex: -1,
  isAnimating: false
})

// Fan geometry: the arc opens from top centre and shifts out to both sides.
const STEP_DEG = 26
const MAX_SPREAD_DEG = 168
// The ring reads as an ellipse in perspective, so the arc the cards stand on
// has to be elliptical too or they drift over the drop zone.
const RADIUS_X = 28
const RADIUS_Y = 27

const pointer = ref({ x: 0, y: 0 })

const onPointerMove = (event: PointerEvent) => {
  pointer.value = {
    x: (event.clientX / window.innerWidth) * 2 - 1,
    y: (event.clientY / window.innerHeight) * 2 - 1
  }
}

onMounted(() => window.addEventListener('pointermove', onPointerMove, { passive: true }))
onBeforeUnmount(() => window.removeEventListener('pointermove', onPointerMove))

const placements = computed(() => {
  const count = props.modifiers.length
  if (!count) return []

  const step = count > 1 ? Math.min(STEP_DEG, MAX_SPREAD_DEG / (count - 1)) : 0

  return props.modifiers.map((modifier, index) => {
    // Zero is top centre; the fan spreads symmetrically from there.
    const angle = (index - (count - 1) / 2) * step
    const radians = (angle * Math.PI) / 180

    // Cards near the top of the arc sit further back in the room.
    const depth = Math.abs(Math.cos(radians))
    const scale = 1 - depth * 0.3
    const lift = depth * 26

    // Parallax: distant cards shift less than near ones as the pointer moves.
    const parallax = (1 - depth) * 10

    return {
      modifier,
      index,
      style: {
        '--x': `${Math.sin(radians) * RADIUS_X}%`,
        '--y': `${-Math.cos(radians) * RADIUS_Y}%`,
        '--scale': String(scale),
        '--lift': `${lift}px`,
        '--px': `${pointer.value.x * parallax}px`,
        '--py': `${pointer.value.y * parallax * 0.5}px`,
        '--delay': `${index * 0.42}s`,
        zIndex: String(10 + Math.round((1 - depth) * 20))
      }
    }
  })
})
</script>

<template>
  <div class="modifier-ring" aria-hidden="false">
    <div
        v-for="placement in placements"
        :key="placement.index"
        class="modifier-ring__slot"
        :class="{ 'modifier-ring__slot--active': props.highlightedIndex === placement.index }"
        :style="placement.style"
    >
      <div class="modifier-ring__float">
        <DiceModifier
            :modifier="placement.modifier"
            :is-highlighted="props.highlightedIndex === placement.index"
            :is-animating="props.isAnimating"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Cards stand in a ring around the drop zone, leaning into the room's
   perspective so they read as levitating in the space rather than pasted on. */
.modifier-ring {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.modifier-ring__slot {
  position: absolute;
  /* Offsets live in left/top so their percentages resolve against the ring,
     not against each card's own box as they would inside translate(). */
  left: calc(50% + var(--x));
  top: calc(50% + var(--y));
  pointer-events: auto;
  /* Cards stay flat and upright — depth is carried by size and brightness, not
     by rotating them, which would distort the artwork. */
  transform:
    translate(-50%, -50%)
    translate(var(--px), calc(var(--py) - var(--lift)))
    scale(var(--scale));
  transition: transform 420ms cubic-bezier(0.22, 0.8, 0.3, 1), filter 300ms ease;
  /* Far cards sit deeper in the haze. */
  filter: brightness(calc(0.55 + var(--scale) * 0.5)) saturate(calc(var(--scale) + 0.1));
}

.modifier-ring__slot--active {
  filter: brightness(1.25) saturate(1.2);
  z-index: 40 !important;
}

/* Levitation is on an inner element so it never fights the placement transform. */
.modifier-ring__float {
  animation: levitate 5.5s ease-in-out infinite;
  animation-delay: var(--delay);
  will-change: transform;
}

.modifier-ring__slot--active .modifier-ring__float {
  animation-duration: 2.4s;
}

@keyframes levitate {
  0%, 100% { transform: translateY(-3px); }
  50% { transform: translateY(5px); }
}

@media (prefers-reduced-motion: reduce) {
  .modifier-ring__float {
    animation: none;
  }

  .modifier-ring__slot {
    transition: none;
  }
}
</style>
